import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { MagnifyingGlassIcon, XMarkIcon, ArrowPathIcon } from '@heroicons/react/24/outline';
import { useLazyQuery } from '@apollo/client';
import { SEARCH_PRODUCTS } from '~/services/store';

interface Product {
  _id: string;
  agent_id: string;
  title: string;
  description: string;
  totalInventory: number;
  priceRangeV2: {
    minVariantPrice: {
      amount: string;
      currencyCode: string;
    };
    maxVariantPrice: {
      amount: string;
      currencyCode: string;
    };
  };
  mainImageURL?: string;
  variants: any[];
  media: any[];
  isActive: boolean;
  onlineStorePreviewUrl?: string;
  score?: number;
}

interface ProductFilterProps {
  agentId: string;
  onSearchResults: (products: Product[], isSearchActive: boolean) => void;
  onClearResults: () => void;
  showAddButton?: boolean;
  onAddProduct?: () => void;
  onRefresh?: () => void;
  isRefreshing?: boolean;
}

export const ProductFilter: React.FC<ProductFilterProps> = ({
  agentId,
  onSearchResults,
  onClearResults,
  showAddButton = false,
  onAddProduct,
  onRefresh,
  isRefreshing = false,
}) => {
  const [t] = useTranslation('store');
  const [searchTerm, setSearchTerm] = useState('');
  const [isSearchActive, setIsSearchActive] = useState(false);

  const [searchProducts, { loading, data, error }] = useLazyQuery(
    SEARCH_PRODUCTS,
    {
      onCompleted: (searchData) => {
        if (searchData?.search_products?.data?.products) {
          onSearchResults(searchData.search_products.data.products, true);
          setIsSearchActive(true);
        }
      },
      onError: (error) => {
        console.error('Product search error:', error);
        // Fallback to empty results on error
        onSearchResults([], false);
        setIsSearchActive(false);
      },
    },
  );

  const handleSearch = () => {
    if (!searchTerm.trim()) {
      handleClearSearch();
      return;
    }

    const variables = {
      data: {
        agent_id: agentId,
        searchTerm: searchTerm.trim(),
        limit: 15,
      },
    };

    searchProducts({ variables });
  };

  const handleClearSearch = () => {
    setIsSearchActive(false);
    onClearResults();
  };

  return (
    <div className="mb-4">
      {/* Search Input and Button */}
      <div className="mb-4 flex items-center gap-2">
        <div className="join flex-1">
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                handleSearch();
              }
            }}
            placeholder={t('search_products_placeholder', 'Search products...')}
            className="input input-bordered join-item w-full"
          />
          <button
            onClick={handleSearch}
            disabled={loading}
            className="btn btn-primary join-item"
          >
            {loading ? (
              <>
                <span className="loading loading-spinner loading-sm"></span>
                {t('searching', 'Searching...')}
              </>
            ) : (
              <>
                <MagnifyingGlassIcon className="h-4 w-4" />
                {t('search', 'Search')}
              </>
            )}
          </button>
        </div>
        {searchTerm && (
          <button
            onClick={() => {
              setSearchTerm('');
              handleClearSearch();
            }}
            className="btn btn-ghost btn-square"
            title="Clear search"
          >
            <XMarkIcon className="h-4 w-4" />
          </button>
        )}
        {onRefresh && (
          <button
            onClick={onRefresh}
            disabled={isRefreshing}
            className="btn btn-outline btn-square"
            title={t('refresh', 'Actualizar inventario')}
          >
            <ArrowPathIcon className={`h-4 w-4 ${isRefreshing ? 'animate-spin' : ''}`} />
          </button>
        )}
        {showAddButton && onAddProduct && (
          <button className="btn btn-secondary gap-2" onClick={onAddProduct}>
            <svg
              className="h-4 w-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M12 4v16m8-8H4"
              ></path>
            </svg>
            {t('add_pruduct')}
          </button>
        )}
      </div>

      {error && (
        <div className="alert alert-error">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-6 w-6 shrink-0 stroke-current"
            fill="none"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
          <div>
            <h3 className="font-bold">{t('search_error', 'Search Error')}</h3>
            <div className="text-xs">
              {t(
                'search_error_message',
                'There was an error searching for products. Please try again.',
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export type { Product };
